import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { View, StyleSheet } from "react-native";
import {
  useGetLearningTtsTokenMutation,
  useSetMcqQuestionBlockAnswerMutation,
  useSetMessageFeedbackMutation,
  useSaveVisualizationChatSummaryMutation,
} from "@axio/graphql/generated/graphql";
import type { LlmStreamContentBlock, LlmStreamTextBlock } from "@axio/graphql/generated/graphql";
import type {
  MessageItem,
  ChatBlock,
  ImageChatBlock,
  VideoChatBlock,
  VisualizationChatBlock,
  InlineResponseChatBlock,
  McqChatBlock,
  SubjectiveChatBlock,
  ObjectiveTransitionData,
  ObjectiveTransitionEvent,
  ObjectiveTransitionItem,
} from "../../companion/companionMessage.types";
import type { ComposerAttachment } from "../../../components/chatNewInterface/chatLayout/messageComposer/ChatComposerBar";
import AITutorMessagesContainer from "../AITutorChatContainer/AITutorMessagesContainer";
import AITutorChatBarContainer from "../../navigation/AITutorChatBarContainer/AITutorChatBarContainer";
import VisualizationModalContainer, {
  type MessageVisualizationBlock,
} from "../VisualizationModalContainer/VisualizationModalContainer";
import CompanionErrorModal from "../../../components/companion/CompanionErrorModal/CompanionErrorModal";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import LottieView from "lottie-react-native";
import LottieUrl from "../../../assets/lottie/aiTutorChatLoadingLottie.json";
import { MathVirtualKeyboardProvider } from "../../../contexts/MathVirtualKeyboardContext";
import { shadowStyles } from "../../../styles/shadow";
import { radii, spacing, semanticColors } from "../../../styles/design-system";
import { ChatAudioPreferencesProvider, useChatAudioPreferences } from "../../../contexts/ChatAudioPreferencesContext";
import useLearningLlmStreamV2 from "../../../hooks/useLearningLlmStreamV2";
import { useStreamBuffer } from "../../../hooks/useStreamBuffer";
import Sentry from "../../../utils/sentry";
import ObjectivesProgressBar from "../../../components/learning-activity/ObjectivesProgressBar/ObjectivesProgressBar";
import type { Objective } from "../../companion/LearningGoalsContainer";

// Narrow `unknown` SSE tool_result fields into the shapes our ChatBlock types expect.
// Until the backend ships a typed discriminated union for tool_result payloads, these
// guard the handler against drift in the wire format.
const asString = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);
const asNullableString = (v: unknown): string | null => (typeof v === "string" ? v : null);
const asStringArray = (v: unknown): string[] =>
  Array.isArray(v) && v.every((c): c is string => typeof c === "string") ? v : [];

function buildObjectiveTransition(
  objectives: Objective[],
  newlyCompletedIds: string[]
): ObjectiveTransitionData | null {
  const newlyCompletedSet = new Set(newlyCompletedIds);
  const items: ObjectiveTransitionItem[] = [];
  let currentAssigned = false;
  for (const obj of objectives) {
    const label = obj.objective?.trim();
    if (!obj.id || !label) continue;
    const completed = newlyCompletedSet.has(obj.id) || obj.isCompleted === true;
    let status: ObjectiveTransitionItem["status"];
    if (completed) {
      status = "completed";
    } else if (!currentAssigned) {
      status = "current";
      currentAssigned = true;
    } else {
      status = "upcoming";
    }
    items.push({ id: obj.id, label, status });
  }
  if (items.length === 0) return null;
  return { objectives: items };
}

function mapServerBlocksToChatBlocks(serverBlocks: LlmStreamContentBlock[], localBlocks: ChatBlock[]): ChatBlock[] {
  return serverBlocks
    .map((sb): ChatBlock | null => {
      if (sb.__typename === "LLMStreamImageBlock") {
        return {
          kind: "image",
          status: sb.status === "completed" ? "completed" : sb.status === "failed" ? "failed" : "loading",
          url: sb.url ?? null,
          title: sb.title ?? null,
          description: sb.description ?? null,
        } satisfies ImageChatBlock;
      }

      if (sb.__typename === "LLMStreamVideoBlock") {
        if (!sb.url) return null;
        return {
          kind: "video",
          videoId: sb.videoId ?? null,
          title: sb.title ?? null,
          url: sb.url,
          thumbnailUrl: sb.thumbnailUrl ?? null,
        } satisfies VideoChatBlock;
      }

      if (sb.__typename === "LLMStreamVisualizationBlock") {
        if (!sb.visualizationId) return null;
        return {
          kind: "visualization",
          visualizationId: sb.visualizationId,
          visualizationType: sb.visualizationType ?? "",
          content: sb.content ?? "",
          visualizationDescription: sb.visualizationDescription ?? null,
        } satisfies VisualizationChatBlock;
      }

      if (sb.__typename === "LLMStreamMcqQuestionBlock") {
        if (!sb.questionId) return null;
        const existing = localBlocks.find((b): b is McqChatBlock => b.kind === "mcq" && b.questionId === sb.questionId);
        const serverIdx =
          "selectedChoiceIndex" in sb && typeof sb.selectedChoiceIndex === "number" ? sb.selectedChoiceIndex : null;
        const serverSubmitted = "answerSubmitted" in sb && sb.answerSubmitted === true;
        return {
          kind: "mcq",
          questionId: sb.questionId,
          question: sb.question,
          choices: sb.choices ?? [],
          selectedChoiceIndex: serverIdx ?? existing?.selectedChoiceIndex ?? null,
          submitted: serverSubmitted || (existing?.submitted ?? false),
        } satisfies McqChatBlock;
      }

      if (sb.__typename === "LLMStreamSubjectiveQuestionBlock") {
        if (!sb.questionId) return null;
        const existing = localBlocks.find(
          (b): b is SubjectiveChatBlock => b.kind === "subjective" && b.questionId === sb.questionId
        );
        return {
          kind: "subjective",
          questionId: sb.questionId,
          question: sb.question,
          answer: existing?.answer ?? "",
          submitted: existing?.submitted ?? false,
        } satisfies SubjectiveChatBlock;
      }

      return null;
    })
    .filter((block): block is ChatBlock => block !== null);
}

const INTRO_TEXTS = [
  "Analyzing your progress and tailoring the conversation...",
  "The more you lean into learning, the faster you'll progress through your objectives.",
  "We detect when answers are copied from AI or other sources - please be original in your responses.",
  "Ask questions when you're curious! The AI loves when you dig deeper into topics.",
  "Don't worry about making mistakes - they're part of the learning process and help you grow.",
  "Take your time to think through each question. Quality of thought matters more than speed.",
  "Your previous conversations help the AI understand your learning style - each session gets more personalized.",
  "Try explaining concepts in your own words - it helps solidify your understanding.",
  "The AI remembers where you've struggled before and will help you strengthen those areas.",
  "Engage authentically - we can tell when responses aren't genuinely your own thinking.",
  "Every question is an opportunity to deepen your understanding of the subject.",
  "Your learning journey is unique - no two students will have the same experience here.",
  "Think critically about each concept before responding - deeper thinking leads to better learning.",
  "Feel free to ask for clarification if something doesn't make sense.",
  "The more engaged you are in the conversation, the more the AI can help you learn.",
  "Building on your past conversations to create the perfect learning experience for you.",
  "Quality responses help the AI understand your thought process and adapt accordingly.",
  "Each interaction helps build a more personalized and effective learning path.",
  "Curiosity and genuine engagement are the keys to accelerating your progress.",
  "Preparing an adaptive conversation based on your unique learning patterns...",
];

const getNextIntroText = (currentText?: string) => {
  const availableTexts = INTRO_TEXTS.filter((text) => text !== currentText);
  return availableTexts[Math.floor(Math.random() * availableTexts.length)];
};

type ChatDataMessage = {
  id?: string;
  content?: string;
  sender?: string;
  created?: string;
  feedback?: string;
  metadata?: Record<string, unknown> | null;
  files?: Array<{ id: string; fileName: string }>;
  todos?: Array<{ id: string; name: string; date?: string | null }>;
  assignments?: Array<{ id: string; name?: string | null; due_date?: string | null }>;
  mcqQuestionBlocks?: Array<{
    id?: string;
    questionId?: string | null;
    question?: string | null;
    choices?: string[] | null;
    selectedChoiceIndex?: number | null;
    answerSubmitted?: boolean | null;
  }>;
  subjectiveQuestionBlocks?: Array<{ id?: string; questionId?: string | null; question?: string | null }>;
  imageBlocks?: Array<{
    id?: string;
    status?: string | null;
    url?: string | null;
  }>;
  videoBlocks?: Array<{
    id?: string;
    videoId?: string | null;
    title?: string | null;
    url?: string | null;
    thumbnailUrl?: string | null;
  }>;
  visualizationBlocks?: Array<{
    id?: string;
    visualizationId?: string | null;
    visualizationType?: string | null;
    visualizationDescription?: string | null;
    content?: string | null;
  }>;
};

type ChatData = {
  id?: string;
  conversation?: ChatDataMessage[];
};

interface AITutorChatContainerV2Props {
  chatData: ChatData | undefined;
  chatLoading: boolean;
  resourceId: string;
  isScreenFocused?: boolean;
  objectives?: Objective[];
  onObjectiveCompleted?: (objectiveIds: string[]) => void;
  onAllObjectivesCompleted?: () => void;
  onStreamingStateChange?: (isStreaming: boolean) => void;
  onStreamCompleted?: () => void;
}

function AITutorChatContentV2({
  chatData,
  chatLoading,
  resourceId,
  isScreenFocused = true,
  objectives,
  onObjectiveCompleted,
  onAllObjectivesCompleted,
  onStreamingStateChange,
  onStreamCompleted,
}: AITutorChatContainerV2Props) {
  const { isAudioOn, playbackSpeed } = useChatAudioPreferences();
  const { startStream, isStreaming } = useLearningLlmStreamV2();

  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [transitions, setTransitions] = useState<ObjectiveTransitionEvent[]>([]);
  const [currentStreamingMessageId, setCurrentStreamingMessageId] = useState<string | null>(null);
  const [resetAutoScroll, setResetAutoScroll] = useState(0);
  const streamingMessageIdRef = useRef<string | null>(null);
  const lastUserMessageIdRef = useRef<string | null>(null);
  const lastCompletedStreamingMessageIdRef = useRef<string | null>(null);
  const hasStreamedLocallyRef = useRef(false);
  const activeChatIdRef = useRef<string | null>(chatData?.id ?? null);
  const objectivesRef = useRef<Objective[]>(objectives);
  useEffect(() => {
    objectivesRef.current = objectives;
  }, [objectives]);

  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorRetry, setErrorRetry] = useState<(() => void) | undefined>(undefined);
  const [feedbackByMessageId, setFeedbackByMessageId] = useState<Record<string, "up" | "down">>({});
  const [setMessageFeedbackMutation] = useSetMessageFeedbackMutation();
  const [setMcqQuestionBlockAnswerMutation] = useSetMcqQuestionBlockAnswerMutation();
  const [getLearningTtsToken] = useGetLearningTtsTokenMutation();
  const [saveVisualizationChatSummaryMutation] = useSaveVisualizationChatSummaryMutation();
  const [activeVisualization, setActiveVisualization] = useState<{
    block: MessageVisualizationBlock;
    chatId: string | null;
  } | null>(null);
  const [vizChatIdByVizId, setVizChatIdByVizId] = useState<Record<string, string>>({});
  const vizSummarizedCountStorageKey = `vizSummarizedCounts-${resourceId}`;
  const vizSummarizedCountByVizId = useRef<Record<string, number> | null>(null);
  if (vizSummarizedCountByVizId.current === null) {
    try {
      const stored =
        typeof window !== "undefined" && window.sessionStorage
          ? window.sessionStorage.getItem(vizSummarizedCountStorageKey)
          : null;
      vizSummarizedCountByVizId.current = stored ? (JSON.parse(stored) as Record<string, number>) : {};
    } catch (_e) {
      vizSummarizedCountByVizId.current = {};
    }
  }
  const vizMessagesStorageKey = `vizMessages-${resourceId}`;
  const [visualizationMessagesByVizId, setVisualizationMessagesByVizId] = useState<Record<string, MessageItem[]>>(
    () => {
      try {
        const stored =
          typeof window !== "undefined" && window.sessionStorage
            ? window.sessionStorage.getItem(`vizMessages-${resourceId}`)
            : null;
        return stored ? (JSON.parse(stored) as Record<string, MessageItem[]>) : {};
      } catch (_e) {
        return {};
      }
    }
  );
  const sendMessageRef = useRef<
    (text: string, options?: { inlineResponseChoiceIndex?: number }) => void | Promise<void>
  >(() => undefined);
  const [isFirstAiStreamInProgress, setIsFirstAiStreamInProgress] = useState(false);
  const [shouldRevealFirstMessageAtTop, setShouldRevealFirstMessageAtTop] = useState(false);
  const [autoReadAloudMessageId, setAutoReadAloudMessageId] = useState<string | null>(null);
  const [introText, setIntroText] = useState(() => INTRO_TEXTS[0] ?? "");
  const isFirstAiStreamInProgressRef = useRef(false);
  const hasCompletedFirstAiStreamRef = useRef(false);
  const pendingAutoReadAloudMessageIdRef = useRef<string | null>(null);
  const getTtsToken = useCallback(async () => {
    const result = await getLearningTtsToken();
    return result.data?.getLearningTtsToken?.token ?? null;
  }, [getLearningTtsToken]);

  const messageReadAloudTts = useMemo(
    () => ({
      resetPlayback: () => undefined,
      suspendPlayback: () => undefined,
      resumePlayback: () => undefined,
      getTtsToken,
    }),
    [getTtsToken]
  );

  const shouldShowIntroLoader = !chatData?.id && isFirstAiStreamInProgress && currentStreamingMessageId !== null;

  const initialMessage = useMemo(
    () => (chatData?.id ? "Let's continue where we left off" : "Please begin by introducing me to this topic"),
    [chatData?.id]
  );

  useEffect(() => {
    hasStreamedLocallyRef.current = false;
    streamingMessageIdRef.current = null;
    lastCompletedStreamingMessageIdRef.current = null;
    isFirstAiStreamInProgressRef.current = false;
    hasCompletedFirstAiStreamRef.current = false;
    pendingAutoReadAloudMessageIdRef.current = null;
    activeChatIdRef.current = null;
    setAutoReadAloudMessageId(null);
    setCurrentStreamingMessageId(null);
    setIsFirstAiStreamInProgress(false);
    setShouldRevealFirstMessageAtTop(false);
    setMessages([]);
    setTransitions([]);
    setIntroText(INTRO_TEXTS[0] ?? "");
    setActiveVisualization(null);
    try {
      const stored =
        typeof window !== "undefined" && window.sessionStorage
          ? window.sessionStorage.getItem(`vizMessages-${resourceId}`)
          : null;
      setVisualizationMessagesByVizId(stored ? (JSON.parse(stored) as Record<string, MessageItem[]>) : {});
    } catch (_e) {
      setVisualizationMessagesByVizId({});
    }
  }, [resourceId]);

  useEffect(() => {
    if (chatData?.id) {
      hasCompletedFirstAiStreamRef.current = true;
      isFirstAiStreamInProgressRef.current = false;
      setIsFirstAiStreamInProgress(false);
      activeChatIdRef.current = chatData.id;
    }
  }, [chatData?.id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (typeof window !== "undefined" && window.sessionStorage) {
        window.sessionStorage.setItem(vizMessagesStorageKey, JSON.stringify(visualizationMessagesByVizId));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [visualizationMessagesByVizId, vizMessagesStorageKey]);

  useEffect(() => {
    onStreamingStateChange?.(currentStreamingMessageId !== null);
  }, [currentStreamingMessageId, onStreamingStateChange]);

  useEffect(() => {
    if (!shouldShowIntroLoader) {
      return;
    }
    setIntroText((prev) => getNextIntroText(prev));
    const intervalId = setInterval(() => {
      setIntroText((prev) => getNextIntroText(prev));
    }, 2500);
    return () => {
      clearInterval(intervalId);
    };
  }, [shouldShowIntroLoader]);

  useEffect(() => {
    if (!chatData?.conversation || hasStreamedLocallyRef.current) {
      return;
    }

    const serverMessages: MessageItem[] = [...chatData.conversation].reverse().map((message, index) => ({
      id: message.id ?? `server-message-${index}`,
      sender: message.sender ?? "ai",
      content: message.content ?? "",
      created: message.created,
      blocks: [
        ...((message.mcqQuestionBlocks ?? []).map(
          (block): McqChatBlock => ({
            kind: "mcq",
            questionId: block.questionId ?? null,
            question: block.question ?? "",
            choices: block.choices ?? [],
            selectedChoiceIndex: typeof block.selectedChoiceIndex === "number" ? block.selectedChoiceIndex : null,
            submitted: block.answerSubmitted === true || typeof block.selectedChoiceIndex === "number",
          })
        ) ?? []),
        ...((message.subjectiveQuestionBlocks ?? []).map(
          (block): SubjectiveChatBlock => ({
            kind: "subjective",
            questionId: block.questionId ?? null,
            question: block.question ?? "",
            answer: "",
            submitted: false,
          })
        ) ?? []),
        ...((message.imageBlocks ?? []).flatMap((block): ImageChatBlock[] => {
          if (!block.url || block.status === "generating") return [];
          return [
            {
              kind: "image",
              status: block.status === "completed" ? "completed" : "failed",
              url: block.url,
            },
          ];
        }) ?? []),
        ...((message.videoBlocks ?? []).flatMap((block): VideoChatBlock[] => {
          if (!block.url) return [];
          return [
            {
              kind: "video",
              videoId: block.videoId ?? null,
              title: block.title ?? null,
              url: block.url,
              thumbnailUrl: block.thumbnailUrl ?? null,
            },
          ];
        }) ?? []),
        ...((message.visualizationBlocks ?? []).flatMap((block): VisualizationChatBlock[] => {
          if (!block.visualizationId || !block.visualizationType || !block.content) return [];
          return [
            {
              kind: "visualization",
              visualizationId: block.visualizationId,
              visualizationType: block.visualizationType,
              visualizationDescription: block.visualizationDescription ?? null,
              content: block.content,
            },
          ];
        }) ?? []),
        ...(() => {
          const inlineData = message.metadata?.inlineResponse as { question?: string; choices?: string[] } | undefined;
          if (
            inlineData?.question &&
            Array.isArray(inlineData.choices) &&
            inlineData.choices.length >= 2 &&
            inlineData.choices.every((c) => typeof c === "string")
          ) {
            const block: InlineResponseChatBlock = {
              kind: "inline_response",
              question: inlineData.question,
              choices: inlineData.choices,
              selectedChoiceIndex: null,
              submitted: false,
            };
            return [block];
          }
          return [];
        })(),
      ],
      files: message.files?.length ? message.files.map((f) => ({ id: f.id, fileName: f.fileName })) : undefined,
      todos: message.todos?.length ? message.todos : undefined,
      assignments: message.assignments?.length
        ? message.assignments.map((a) => ({ id: a.id, name: a.name ?? undefined, due_date: a.due_date ?? undefined }))
        : undefined,
    }));

    setMessages(serverMessages);

    const initialFeedback: Record<string, "up" | "down"> = {};
    for (const message of chatData.conversation) {
      const fb = message.feedback;
      if (fb === "up" || fb === "down") {
        initialFeedback[message.id ?? ""] = fb;
      }
    }
    setFeedbackByMessageId(initialFeedback);
  }, [chatData]);

  const applyStreamText = useCallback((text: string, isFinal: boolean) => {
    const streamingId = streamingMessageIdRef.current;
    if (!streamingId) return;
    if (text) {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === streamingId ? { ...message, content: `${message.content ?? ""}${text}` } : message
        )
      );
    }
    if (isFinal) {
      lastCompletedStreamingMessageIdRef.current = streamingId;
      streamingMessageIdRef.current = null;
      setCurrentStreamingMessageId(null);
    }
  }, []);

  const { pushChunk, flush: flushBuffer, reset: resetBuffer } = useStreamBuffer(applyStreamText);

  const handleStreamStart = useCallback(
    (message: string, attachment?: ComposerAttachment | null, meta?: { isAutoStart?: boolean }) => {
      resetBuffer();
      hasStreamedLocallyRef.current = true;
      const isFirstAiStream = Boolean(meta?.isAutoStart) && !chatData?.id && !hasCompletedFirstAiStreamRef.current;
      if (isFirstAiStream) {
        isFirstAiStreamInProgressRef.current = true;
        setIsFirstAiStreamInProgress(true);
        setShouldRevealFirstMessageAtTop(false);
      } else {
        isFirstAiStreamInProgressRef.current = false;
        setIsFirstAiStreamInProgress(false);
        hasCompletedFirstAiStreamRef.current = true;
      }
      pendingAutoReadAloudMessageIdRef.current = null;
      setAutoReadAloudMessageId(null);
      setResetAutoScroll((prev) => prev + 1);
      const userMessageId = `user-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const aiTempId = `ai-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      streamingMessageIdRef.current = aiTempId;
      lastUserMessageIdRef.current = meta?.isAutoStart ? null : userMessageId;
      lastCompletedStreamingMessageIdRef.current = aiTempId;
      setCurrentStreamingMessageId(aiTempId);
      const files = attachment ? [{ id: attachment.id, fileName: attachment.name }] : undefined;

      setMessages((prev) => {
        const nextMessages = [...prev];
        if (!meta?.isAutoStart) {
          nextMessages.push({ id: userMessageId, sender: "human", content: message, files });
        }
        nextMessages.push({ id: aiTempId, sender: "ai", content: "" });
        return nextMessages;
      });
    },
    [chatData?.id, resetBuffer]
  );

  const handleStreamChunk = useCallback(
    ({ text, isFinalChunk }: { text: string; isFinalChunk?: boolean }) => {
      if (!streamingMessageIdRef.current) return;
      pushChunk(text, isFinalChunk);
    },
    [pushChunk]
  );

  const handleToolResult = useCallback((payload: { tool: string; result: Record<string, unknown> }) => {
    const id = streamingMessageIdRef.current;
    if (!id) return;
    const res = payload.result;

    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;

        if (payload.tool === "image") {
          const url = asNullableString(res.url);
          const title = asNullableString(res.title);
          let updated = false;
          const blocks = (m.blocks ?? []).map((b) => {
            if (!updated && b.kind === "image" && b.status === "loading") {
              updated = true;
              return { ...b, status: url ? "completed" : "failed", url, title } as ImageChatBlock;
            }
            return b;
          });
          if (!updated) {
            blocks.push({ kind: "image", status: url ? "completed" : "failed", url } as ImageChatBlock);
          }
          return { ...m, blocks };
        }

        if (payload.tool === "video") {
          if (typeof res.url !== "string") return m;
          const block: VideoChatBlock = {
            kind: "video",
            videoId: asNullableString(res.videoId),
            title: asNullableString(res.title),
            url: res.url,
            thumbnailUrl: asNullableString(res.thumbnailUrl),
          };
          return { ...m, blocks: [...(m.blocks ?? []), block] };
        }

        if (payload.tool === "visualization") {
          if (typeof res.visualizationId === "string" && res.visualizationId.length > 0) {
            const block: VisualizationChatBlock = {
              kind: "visualization",
              visualizationId: res.visualizationId,
              visualizationType: asString(res.visualizationType),
              visualizationDescription: asNullableString(res.visualizationDescription),
              content: asString(res.content),
            };
            return { ...m, blocks: [...(m.blocks ?? []), block] };
          }
          return m;
        }

        if (payload.tool === "mcq") {
          const questionId = asNullableString(res.questionId);
          const question = asNullableString(res.question);
          const choices = asStringArray(res.choices);
          if (!questionId || !question || choices.length < 2) {
            Sentry.captureMessage("[ILEv2] Malformed mcq tool_result payload — skipping block", {
              level: "warning",
              extra: { res },
            });
            return m;
          }
          const block: McqChatBlock = {
            kind: "mcq",
            questionId,
            question,
            choices,
            selectedChoiceIndex: null,
            submitted: false,
          };
          return { ...m, content: "", blocks: [...(m.blocks ?? []), block] };
        }

        if (payload.tool === "subjective") {
          const questionId = asNullableString(res.questionId);
          const question = asNullableString(res.question);
          if (!questionId || !question) {
            Sentry.captureMessage("[ILEv2] Malformed subjective tool_result payload — skipping block", {
              level: "warning",
              extra: { res },
            });
            return m;
          }
          const block: SubjectiveChatBlock = {
            kind: "subjective",
            questionId,
            question,
            answer: "",
            submitted: false,
          };
          return { ...m, content: "", blocks: [...(m.blocks ?? []), block] };
        }

        return m;
      })
    );
  }, []);

  const handleChoiceSelect = useCallback(
    (messageId: string, blockIndex: number, choiceIndex: number) => {
      if (isStreaming) return;
      // Extract needed data before mutating state to avoid reading closure-mutated variables
      const msg = messages.find((m) => m.id === messageId);
      const block = msg?.blocks?.[blockIndex];
      if (!block || (block.kind !== "mcq" && block.kind !== "inline_response") || block.submitted) return;
      const choiceText = block.choices[choiceIndex];
      if (!choiceText) return;
      const isMcq = block.kind === "mcq";
      const questionId = isMcq ? block.questionId || null : null;

      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m;
          const blocks = (m.blocks ?? []).map((b, i) => {
            if (i !== blockIndex || (b.kind !== "mcq" && b.kind !== "inline_response")) return b;
            if (b.submitted) return b;
            return { ...b, selectedChoiceIndex: choiceIndex, submitted: true };
          });
          return { ...m, blocks };
        })
      );

      void sendMessageRef.current(choiceText, isMcq ? { inlineResponseChoiceIndex: choiceIndex } : undefined);

      if (isMcq && questionId) {
        void setMcqQuestionBlockAnswerMutation({
          variables: {
            messageId,
            questionId,
            selectedChoiceIndex: choiceIndex,
          },
        })
          .then((res) => {
            const ok = res.data?.setMcqQuestionBlockAnswer?.success;
            if (ok === false) {
              setErrorTitle("Could not save answer");
              setErrorMessage(res.data?.setMcqQuestionBlockAnswer?.message ?? "Please try again.");
              setErrorModalVisible(true);
            }
          })
          .catch((error) => {
            Sentry.captureException(error);
            setErrorTitle("Could not save answer");
            setErrorMessage("Unable to save your selection. Please try again.");
            setErrorModalVisible(true);
          });
      }
    },
    [isStreaming, messages, setMcqQuestionBlockAnswerMutation]
  );

  const handleStreamComplete = useCallback(
    (
      chatId?: string,
      serverMessageId?: string,
      serverBlocks?: LlmStreamContentBlock[],
      metadata?: Record<string, unknown>
    ) => {
      // Drain any remaining buffered text synchronously so it's applied to the
      // still-temp message ID before we clear streamingMessageIdRef below.
      flushBuffer();
      const completedMessageId = streamingMessageIdRef.current ?? lastCompletedStreamingMessageIdRef.current;
      if (isFirstAiStreamInProgressRef.current) {
        hasCompletedFirstAiStreamRef.current = true;
        isFirstAiStreamInProgressRef.current = false;
        setIsFirstAiStreamInProgress(false);
        setShouldRevealFirstMessageAtTop(true);
      }
      streamingMessageIdRef.current = null;
      setCurrentStreamingMessageId(null);
      if (chatId) {
        activeChatIdRef.current = chatId;
      }
      if (completedMessageId && isAudioOn) {
        pendingAutoReadAloudMessageIdRef.current = completedMessageId;
        setAutoReadAloudMessageId(completedMessageId);
      }
      // Replace temp message ID with server-assigned ID, reconcile blocks, and append inline response
      if (completedMessageId) {
        const realId = serverMessageId && serverMessageId !== completedMessageId ? serverMessageId : null;
        const hasServerBlocks = serverBlocks && serverBlocks.length > 0;
        const hasQuestionBlock = hasServerBlocks
          ? serverBlocks.some((sb) => sb.type === "mcq_question" || sb.type === "subjective_question")
          : false;
        const textBlock = hasServerBlocks
          ? serverBlocks.find((sb): sb is LlmStreamTextBlock => sb.__typename === "LLMStreamTextBlock")
          : undefined;
        const serverTextContent = textBlock?.text ?? "";
        const inlineResponseData = metadata?.inlineResponse as
          | { question?: string; choices?: string[]; source?: string }
          | undefined;
        const hasValidInlineResponse =
          !!inlineResponseData?.source && !!inlineResponseData.question && !!inlineResponseData.choices?.length;

        if (realId || hasServerBlocks || hasValidInlineResponse) {
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== completedMessageId) return m;
              const reconciled = hasServerBlocks ? mapServerBlocksToChatBlocks(serverBlocks!, m.blocks ?? []) : null;
              let updated = {
                ...m,
                ...(realId ? { id: realId } : {}),
                ...(reconciled && reconciled.length > 0
                  ? { content: hasQuestionBlock ? serverTextContent : m.content, blocks: reconciled }
                  : {}),
              };
              if (hasValidInlineResponse) {
                const blocks = updated.blocks ?? [];
                const alreadyExists = blocks.some(
                  (b) => b.kind === "inline_response" && b.question === inlineResponseData!.question
                );
                if (!alreadyExists) {
                  const inlineResponseBlock: InlineResponseChatBlock = {
                    kind: "inline_response",
                    question: inlineResponseData!.question!,
                    choices: inlineResponseData!.choices!,
                    selectedChoiceIndex: null,
                    submitted: false,
                  };
                  updated = { ...updated, blocks: [...blocks, inlineResponseBlock] };
                }
              }
              return updated;
            })
          );
        }
      }
      onStreamCompleted?.();
    },
    [isAudioOn, onStreamCompleted, flushBuffer]
  );

  const handleRevealedFirstMessageAtTop = useCallback(() => {
    setShouldRevealFirstMessageAtTop(false);
  }, []);

  const handleStreamError = useCallback(
    ({ title, message, onRetry }: { title: string; message: string; onRetry?: () => void }) => {
      resetBuffer();
      const streamingId = streamingMessageIdRef.current;
      if (streamingId) {
        setMessages((prev) => prev.filter((item) => item.id !== streamingId));
      }
      if (isFirstAiStreamInProgressRef.current) {
        isFirstAiStreamInProgressRef.current = false;
        setIsFirstAiStreamInProgress(false);
        setShouldRevealFirstMessageAtTop(false);
      }
      streamingMessageIdRef.current = null;
      setCurrentStreamingMessageId(null);
      setErrorTitle(title);
      setErrorMessage(message);
      setErrorRetry(() => onRetry);
      setErrorModalVisible(true);
      pendingAutoReadAloudMessageIdRef.current = null;
      setAutoReadAloudMessageId(null);
    },
    [resetBuffer]
  );

  const handleFeedback = useCallback(
    async (messageId: string, value: "up" | "down" | null) => {
      setFeedbackByMessageId((prev) => {
        if (value === null) {
          const next = { ...prev };
          delete next[messageId];
          return next;
        }
        return { ...prev, [messageId]: value };
      });
      datadogRum.addAction("message_feedback", { messageId, chatType: "ile", value });
      try {
        await setMessageFeedbackMutation({ variables: { messageId, value: value ?? "" } });
      } catch {
        setErrorTitle("Feedback Error");
        setErrorMessage("Unable to submit feedback. Please try again.");
        setErrorModalVisible(true);
      }
    },
    [setMessageFeedbackMutation]
  );

  const handleVisualizationModalClose = useCallback(async () => {
    if (!activeVisualization) return;
    const { block, chatId } = activeVisualization;
    const vizId = block.visualizationId;
    const modalMessages = visualizationMessagesByVizId[vizId] ?? [];
    setActiveVisualization(null);

    const counts = vizSummarizedCountByVizId.current ?? {};
    const lastSummarizedCount = counts[vizId] ?? 0;
    if (modalMessages.length === 0 || modalMessages.length === lastSummarizedCount || !chatId) return;

    // Mark this position as handled before the async call so any outcome (empty
    // summary, network error, etc.) prevents a duplicate send on the next close.
    const updated = { ...counts, [vizId]: modalMessages.length };
    vizSummarizedCountByVizId.current = updated;
    try {
      window.sessionStorage?.setItem(vizSummarizedCountStorageKey, JSON.stringify(updated));
    } catch (_e) {
      // sessionStorage unavailable — counts won't persist across refresh
    }

    try {
      const result = await saveVisualizationChatSummaryMutation({
        variables: {
          chatId,
          visualizationId: vizId,
          visualizationType: block.visualizationType,
          messages: modalMessages.map((m) => ({
            sender: m.sender === "human" ? "human" : "ai",
            content: m.content ?? "",
          })),
        },
      });
      const summaryContent = result.data?.saveVisualizationChatSummary?.summaryContent;
      const messageId = result.data?.saveVisualizationChatSummary?.messageId;
      if (summaryContent && messageId) {
        setMessages((prev) => [
          ...prev,
          { id: messageId, sender: "ai", content: summaryContent, created: new Date().toISOString() },
        ]);
      }
    } catch (error) {
      Sentry.captureMessage("[VisualizationChat] summary save failed", {
        level: "warning",
        extra: { visualizationId: vizId, error },
      });
    }
  }, [activeVisualization, visualizationMessagesByVizId, saveVisualizationChatSummaryMutation]);

  const handleObjectiveCompleted = useCallback(
    (objectiveIds: string[]) => {
      const anchorMessageId = lastUserMessageIdRef.current;
      if (objectiveIds.length > 0 && anchorMessageId) {
        const transition = buildObjectiveTransition(objectivesRef.current, objectiveIds);
        if (transition) {
          setTransitions((prev) => [
            ...prev,
            {
              id: `obj-transition-${objectiveIds.join("-")}-${Date.now()}`,
              transition,
              afterMessageId: anchorMessageId,
            },
          ]);
        }
      }
      onObjectiveCompleted?.(objectiveIds);
    },
    [onObjectiveCompleted]
  );

  const sendMessage = useCallback(
    async (text: string, options?: { inlineResponseChoiceIndex?: number }) => {
      if (isStreaming) return;
      handleStreamStart(text);
      try {
        await startStream(text, {
          chatId: activeChatIdRef.current ?? undefined,
          contentNodeId: resourceId,
          inlineResponseChoiceIndex: options?.inlineResponseChoiceIndex,
          onChunk: ({ text: chunk, isFinalChunk }) => {
            handleStreamChunk({ text: chunk, isFinalChunk });
          },
          onChatId: (nextChatId) => {
            activeChatIdRef.current = nextChatId;
          },
          onObjectiveCompleted: ({ objectiveIds }) => {
            handleObjectiveCompleted(objectiveIds);
          },
          onAllObjectivesCompleted: () => {
            onAllObjectivesCompleted?.();
          },
          onComplete: ({ event }) => {
            handleStreamComplete(event?.chatId, event?.messageId, event?.blocks, event?.metadata);
          },
          onToolResult: handleToolResult,
          onError: ({ error }) => {
            Sentry.captureException(error);
            handleStreamError({
              title: "Failed to send message",
              message: error.message || "An error occurred. Please try again.",
            });
          },
        });
      } catch (error) {
        Sentry.captureException(error);
        handleStreamError({
          title: "Failed to send message",
          message:
            error instanceof Error
              ? error.message || "An error occurred. Please try again."
              : "An error occurred. Please try again.",
        });
      }
    },
    [
      isStreaming,
      resourceId,
      startStream,
      handleStreamStart,
      handleStreamChunk,
      handleObjectiveCompleted,
      onAllObjectivesCompleted,
      handleStreamComplete,
      handleStreamError,
      handleToolResult,
    ]
  );

  sendMessageRef.current = sendMessage;

  const hasPendingQuestion = useMemo(() => {
    const lastMessage = messages[messages.length - 1];
    return lastMessage?.blocks?.some((b) => (b.kind === "mcq" || b.kind === "subjective") && !b.submitted) ?? false;
  }, [messages]);

  const hasPendingMcq = useMemo(() => {
    const lastMessage = messages[messages.length - 1];
    return lastMessage?.blocks?.some((b) => b.kind === "mcq" && !b.submitted) ?? false;
  }, [messages]);

  return (
    <MathVirtualKeyboardProvider>
      <View style={styles.container}>
        <View style={styles.content}>
          <View style={styles.messagesArea}>
            {shouldShowIntroLoader ? (
              <View style={styles.loaderWrapper}>
                <View style={[styles.loaderContainer, shadowStyles.allAroundShadow]}>
                  <LottieView autoPlay source={LottieUrl} speed={1} loop={true} style={{ width: 100, height: 100 }} />
                  <CustomText
                    text="Loading your personalized learning experience"
                    useTranslationText={false}
                    size="l"
                    weight="bold"
                    style={{ textAlign: "center" }}
                    aria-level="2"
                    role="heading"
                  />
                  <CustomText
                    text={introText}
                    useTranslationText={false}
                    size="m"
                    weight="regular"
                    style={{ marginTop: 10, textAlign: "center" }}
                  />
                </View>
              </View>
            ) : (
              <AITutorMessagesContainer
                resourceId={resourceId}
                messages={messages}
                transitions={transitions}
                isStreaming={currentStreamingMessageId !== null}
                currentStreamingMessageId={currentStreamingMessageId}
                resetAutoScroll={resetAutoScroll}
                shouldRevealFirstMessageAtTop={shouldRevealFirstMessageAtTop}
                onRevealedFirstMessageAtTop={handleRevealedFirstMessageAtTop}
                feedbackByMessageId={feedbackByMessageId}
                onFeedback={handleFeedback}
                messageReadAloudTts={messageReadAloudTts}
                autoReadAloudEnabled={isAudioOn}
                autoReadAloudMessageId={autoReadAloudMessageId}
                playbackSpeed={playbackSpeed}
                isScreenFocused={isScreenFocused}
                onOpenVisualization={(block) => setActiveVisualization({ block, chatId: activeChatIdRef.current })}
                onChoiceSelect={handleChoiceSelect}
              />
            )}
            <View style={styles.chatInputWrapper}>
              {objectives && objectives.length > 0 && (
                <ObjectivesProgressBar
                  objectives={objectives}
                  // sendMessage and AITutorChatBarContainer share the same isStreaming lock from
                  // useLearningLlmStreamV2 — only one stream can be in-flight at a time.
                  isStreaming={isStreaming}
                  hasPendingQuestion={hasPendingQuestion}
                  onMoveOn={() => sendMessage("I'm ready to move on to the next objective")}
                />
              )}
              <AITutorChatBarContainer
                key={resourceId}
                chatId={chatData?.id ?? null}
                contentNodeId={resourceId}
                startStream={startStream}
                isStreaming={isStreaming}
                hasPendingMcq={hasPendingMcq}
                autoStart={!chatLoading && !chatData?.id}
                initialMessage={initialMessage}
                onStreamStart={handleStreamStart}
                onStreamChunk={handleStreamChunk}
                onObjectiveCompleted={handleObjectiveCompleted}
                onAllObjectivesCompleted={onAllObjectivesCompleted}
                onStreamComplete={handleStreamComplete}
                onStreamError={handleStreamError}
                onToolResult={handleToolResult}
              />
              <CustomText
                useTranslationText
                size="xs"
                weight="regular"
                text="ai_tutor_chat_disclaimer"
                style={styles.disclaimerText}
              />
            </View>
          </View>
        </View>
        <CompanionErrorModal
          visible={errorModalVisible}
          title={errorTitle}
          message={errorMessage}
          onRetry={errorRetry}
          onClose={() => {
            setErrorModalVisible(false);
            setErrorRetry(undefined);
          }}
        />
        {activeVisualization ? (
          <VisualizationModalContainer
            visible={true}
            block={activeVisualization.block}
            vizChatId={vizChatIdByVizId[activeVisualization.block.visualizationId] ?? null}
            onVizChatId={(chatId) => {
              const vizId = activeVisualization.block.visualizationId;
              setVizChatIdByVizId((prev) => ({ ...prev, [vizId]: chatId }));
            }}
            contentNodeId={resourceId}
            messages={visualizationMessagesByVizId[activeVisualization.block.visualizationId] ?? []}
            onMessagesChange={(updater) => {
              const vizId = activeVisualization.block.visualizationId;
              setVisualizationMessagesByVizId((prev) => ({
                ...prev,
                [vizId]: updater(prev[vizId] ?? []),
              }));
            }}
            onClose={handleVisualizationModalClose}
          />
        ) : null}
      </View>
    </MathVirtualKeyboardProvider>
  );
}

function AITutorChatContainerV2(props: AITutorChatContainerV2Props) {
  return (
    <ChatAudioPreferencesProvider>
      <AITutorChatContentV2 {...props} />
    </ChatAudioPreferencesProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: semanticColors.surface.white,
  },
  content: {
    flex: 1,
    position: "relative",
  },
  messagesArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: semanticColors.surface.light,
  },
  chatInputWrapper: {
    width: "100%",
    maxWidth: 1152,
    alignSelf: "center",
    zIndex: 900,
  },
  disclaimerText: {
    textAlign: "center",
    paddingBottom: spacing.xs,
    color: semanticColors.typography.textLight,
  },
  loaderWrapper: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loaderContainer: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: semanticColors.surface.white,
    borderRadius: radii.lg,
    borderWidth: 0,
    maxWidth: 500,
    width: "90%",
    padding: spacing["2xl"],
  },
});

export default AITutorChatContainerV2;
